import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Eye,
  DollarSign,
  Download
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { invoicesApi, salesOrdersApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Invoices = () => {
  const { isAdmin, isSales } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  
  const [formData, setFormData] = useState({
    sales_order_id: '',
    due_date: '',
    notes: ''
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, soRes] = await Promise.all([
        invoicesApi.getAll(),
        salesOrdersApi.getAll()
      ]);
      setInvoices(invRes.data || []);
      // Filter sales orders that don't have invoices yet
      const invoicedOrderIds = (invRes.data || []).map(inv => inv.sales_order_id);
      const availableOrders = (soRes.data || []).filter(
        order => !invoicedOrderIds.includes(order.id) && 
        ['confirmed', 'processing', 'shipped', 'delivered'].includes(order.status)
      );
      setSalesOrders(availableOrders);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoicesApi.create(formData);
      toast.success('Invoice created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error creating invoice:', err);
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handlePayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const response = await invoicesApi.recordPayment(paymentModal.id, {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        reference: paymentData.reference || null,
        notes: paymentData.notes || null
      });
      toast.success(`Payment recorded (MOCKED). Reference: ${response.data.reference}`);
      setPaymentModal(null);
      setPaymentData({ amount: '', payment_method: 'bank_transfer', reference: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      sales_order_id: '',
      due_date: '',
      notes: ''
    });
  };

  const getPaymentBadgeClass = (status) => {
    const classes = {
      pending: 'payment-pending',
      partial: 'payment-partial',
      paid: 'payment-paid',
      overdue: 'payment-overdue',
    };
    return classes[status] || 'payment-pending';
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.payment_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="invoices-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="skeleton h-96 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">{invoices.length} total invoices</p>
        </div>
        {(isAdmin || isSales) && (
          <Button 
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            data-testid="create-invoice-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Invoice
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No invoices found</h3>
              <p className="text-slate-500">Create an invoice from a sales order.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{invoice.customer_name}</p>
                          {invoice.customer_company && (
                            <p className="text-xs text-slate-500">{invoice.customer_company}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">${invoice.total.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        ${invoice.amount_paid.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        ${invoice.balance_due.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getPaymentBadgeClass(invoice.payment_status)} capitalize`}>
                          {invoice.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => setViewInvoice(invoice)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(isAdmin || isSales) && invoice.payment_status !== 'paid' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 text-green-600"
                              onClick={() => setPaymentModal(invoice)}
                            >
                              <DollarSign className="w-3 h-3 mr-1" />
                              Pay
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Sales Order</Label>
                <Select 
                  value={formData.sales_order_id} 
                  onValueChange={(value) => setFormData({ ...formData, sales_order_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sales order" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesOrders.length === 0 ? (
                      <SelectItem value="none" disabled>No eligible orders</SelectItem>
                    ) : (
                      salesOrders.map(order => (
                        <SelectItem key={order.id} value={order.id}>
                          {order.order_number} - {order.customer_name} (${order.total.toFixed(2)})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label className="form-label">Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Payment terms or additional information..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-orange-600 hover:bg-orange-700"
                disabled={salesOrders.length === 0}
                data-testid="submit-invoice-button"
              >
                Create Invoice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={!!paymentModal} onOpenChange={() => setPaymentModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-md p-3 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-slate-500">Invoice:</span>
                <span className="font-mono">{paymentModal?.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Balance Due:</span>
                <span className="font-mono text-red-600">${paymentModal?.balance_due.toFixed(2)}</span>
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              <strong>Note:</strong> This is a MOCKED Airwallex integration. In production, this would connect to Airwallex payment gateway.
            </div>

            <div>
              <Label className="form-label">Amount</Label>
              <Input
                type="number"
                step="0.01"
                max={paymentModal?.balance_due}
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="payment-amount-input"
              />
            </div>

            <div>
              <Label className="form-label">Payment Method</Label>
              <Select 
                value={paymentData.payment_method} 
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="form-label">Reference (Optional)</Label>
              <Input
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                placeholder="Transaction reference"
              />
            </div>

            <div>
              <Label className="form-label">Notes (Optional)</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPaymentModal(null)}>
              Cancel
            </Button>
            <Button onClick={handlePayment} className="bg-green-600 hover:bg-green-700" data-testid="submit-payment-button">
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice {viewInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Customer</span>
                  <p className="font-medium">{viewInvoice.customer_name}</p>
                  {viewInvoice.customer_company && (
                    <p className="text-xs text-slate-500">{viewInvoice.customer_company}</p>
                  )}
                </div>
                <div>
                  <span className="text-slate-500">Sales Order</span>
                  <p className="font-mono">{viewInvoice.sales_order_number}</p>
                </div>
                <div>
                  <span className="text-slate-500">Due Date</span>
                  <p className="font-medium">{new Date(viewInvoice.due_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <Badge className={`${getPaymentBadgeClass(viewInvoice.payment_status)} capitalize mt-1`}>
                    {viewInvoice.payment_status}
                  </Badge>
                </div>
              </div>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewInvoice.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">${item.unit_price.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">
                          ${(item.quantity * item.unit_price).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-slate-50 p-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-mono">${viewInvoice.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span className="font-mono">${viewInvoice.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2">
                    <span>Total</span>
                    <span className="font-mono">${viewInvoice.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Amount Paid</span>
                    <span className="font-mono">${viewInvoice.amount_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-red-600 font-bold">
                    <span>Balance Due</span>
                    <span className="font-mono">${viewInvoice.balance_due.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
